import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Bell, CalendarDays, CheckCircle2, Circle, Flag, List as ListIcon, Mail, Plus, Repeat, Star, Sun, Trash2, X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { TaskItem, TaskList, TaskStep } from "@/api/mailApi";
import {
    addDaysKey, buildRule, DAY_CODES, DAY_SHORT, describeRule, firstOccurrenceKey, formatDay, fromLocalInput,
    dayKey, isOverdue, keyToInstant, newId, parseRule, stepProgress, toLocalInput, weekdayOfKey, WEEKDAYS_RULE,
    type RepeatFreq, type RepeatRule
} from "./taskModel";

interface TaskDetailsProps {
    task: TaskItem;
    lists: TaskList[];
    tz: string;
    today: string;
    onClose: () => void;
    onSave: (task: TaskItem) => void;
    onToggle: (task: TaskItem) => void;
    onDelete: (task: TaskItem) => void;
    className?: string;
}

/** To Do's right-hand details pane. Text fields save on blur; everything else saves at once. */
export function TaskDetails({ task, lists, tz, today, onClose, onSave, onToggle, onDelete, className }: TaskDetailsProps) {
    const navigate = useNavigate();
    const [title, setTitle] = useState(task.title);
    const [notes, setNotes] = useState(task.notes ?? "");
    const [newStep, setNewStep] = useState("");
    const [pickReminder, setPickReminder] = useState(false);
    const [pickDue, setPickDue] = useState(false);
    const [customRepeat, setCustomRepeat] = useState(false);

    useEffect(() => {
        setTitle(task.title);
        setNotes(task.notes ?? "");
        setPickReminder(false);
        setPickDue(false);
        setCustomRepeat(false);
        // Re-seed only when a different task is opened, not on every save echo.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [task.id]);

    const patch = (p: Partial<TaskItem>) => onSave({ ...task, ...p });
    const setSteps = (steps: TaskStep[]) => patch({ steps });
    const progress = stepProgress(task);
    const inMyDay = task.myDayDate === today;

    const setRepeat = (rule: string | null) => {
        if (!rule) { patch({ recurrence: null }); return; }
        // A repeating task needs a date to repeat from.
        const r = parseRule(rule);
        const dueAt = task.dueAt ?? (r ? keyToInstant(firstOccurrenceKey(r, today), tz).toISOString() : null);
        patch({ recurrence: rule, dueAt });
    };
    const setDueKey = (key: string | null) => patch({ dueAt: key ? keyToInstant(key, tz).toISOString() : null });

    const now = new Date();
    const laterToday = new Date(Math.ceil((now.getTime() + 3 * 3_600_000) / 3_600_000) * 3_600_000);
    const nextMonday = addDaysKey(today, 8 - weekdayOfKey(today));

    return (
        <aside className={cn("flex h-full flex-col border-l bg-card", className)} aria-label="Task details">
            <div className="flex items-center justify-end gap-1 border-b px-2 py-1.5">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} aria-label="Close details">
                    <X className="h-4 w-4" />
                </Button>
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
                {/* Title */}
                <div className="rounded-md border bg-background p-2">
                    <div className="flex items-start gap-2">
                        <button
                            type="button"
                            onClick={() => onToggle(task)}
                            aria-label={task.isCompleted ? "Mark incomplete" : "Mark complete"}
                            className="mt-1 shrink-0 text-muted-foreground hover:text-primary"
                        >
                            {task.isCompleted ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <Circle className="h-5 w-5" />}
                        </button>
                        <textarea
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            onBlur={() => {
                                const t = title.trim();
                                if (t && t !== task.title) patch({ title: t });
                                else setTitle(task.title);
                            }}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
                            rows={2}
                            aria-label="Task title"
                            className={cn(
                                "min-w-0 flex-1 resize-none bg-transparent text-base font-semibold outline-none",
                                task.isCompleted && "text-muted-foreground line-through"
                            )}
                        />
                        <button
                            type="button"
                            onClick={() => patch({ isImportant: !task.isImportant })}
                            aria-label={task.isImportant ? "Remove importance" : "Mark as important"}
                            aria-pressed={task.isImportant}
                            className="mt-1 shrink-0 text-muted-foreground hover:text-primary"
                        >
                            <Star className={cn("h-5 w-5", task.isImportant && "fill-primary text-primary")} />
                        </button>
                    </div>

                    {/* Steps */}
                    {progress ? <p className="mt-1 pl-7 text-xs text-muted-foreground">{progress} steps done</p> : null}
                    <ul className="mt-1 space-y-0.5 pl-6">
                        {task.steps.map((s) => (
                            <StepRow
                                key={s.id}
                                step={s}
                                onChange={(next) => setSteps(task.steps.map((x) => (x.id === s.id ? next : x)))}
                                onDelete={() => setSteps(task.steps.filter((x) => x.id !== s.id))}
                            />
                        ))}
                    </ul>
                    <form
                        className="mt-1 flex items-center gap-2 pl-7"
                        onSubmit={(e) => {
                            e.preventDefault();
                            const t = newStep.trim();
                            if (!t) return;
                            setSteps([...task.steps, { id: newId(), title: t.slice(0, 512), isCompleted: false }]);
                            setNewStep("");
                        }}
                    >
                        <Plus className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                        <input
                            value={newStep}
                            onChange={(e) => setNewStep(e.target.value)}
                            placeholder={task.steps.length ? "Next step" : "Add step"}
                            aria-label="Add step"
                            className="h-7 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-primary"
                        />
                    </form>
                </div>

                {/* My Day */}
                <button
                    type="button"
                    onClick={() => patch({ myDayDate: inMyDay ? null : today })}
                    className={cn(
                        "flex w-full items-center gap-3 rounded-md border bg-background px-3 py-2.5 text-left text-sm hover:bg-accent",
                        inMyDay && "text-primary"
                    )}
                >
                    <Sun className="h-4 w-4" />
                    <span className="flex-1">{inMyDay ? "Added to My Day" : "Add to My Day"}</span>
                    {inMyDay ? <X className="h-3.5 w-3.5 text-muted-foreground" aria-label="Remove from My Day" /> : null}
                </button>

                <div className="divide-y rounded-md border bg-background">
                    {/* Reminder */}
                    <div className="px-3 py-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button type="button" className={cn("flex w-full items-center gap-3 text-left text-sm", task.reminderAt && "text-primary")}>
                                    <Bell className="h-4 w-4" />
                                    <span className="flex-1">
                                        {task.reminderAt ? `Remind me at ${formatDay(task.reminderAt, tz, today)}` : "Remind me"}
                                    </span>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-60">
                                <DropdownMenuItem onSelect={() => patch({ reminderAt: laterToday.toISOString() })}>
                                    Later today ({formatDay(laterToday.toISOString(), tz, today)})
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => patch({ reminderAt: keyToInstant(addDaysKey(today, 1), tz, 9).toISOString() })}>
                                    Tomorrow, 9:00 AM
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => patch({ reminderAt: keyToInstant(nextMonday, tz, 9).toISOString() })}>
                                    Next week (Mon, 9:00 AM)
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setPickReminder(true)}>Pick a date &amp; time…</DropdownMenuItem>
                                {task.reminderAt ? (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem className="text-destructive" onSelect={() => patch({ reminderAt: null })}>
                                            Remove reminder
                                        </DropdownMenuItem>
                                    </>
                                ) : null}
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {pickReminder ? (
                            <Input
                                type="datetime-local"
                                autoFocus
                                defaultValue={toLocalInput(task.reminderAt, tz)}
                                aria-label="Reminder date and time"
                                className="mt-2 h-8"
                                onChange={(e) => {
                                    const iso = fromLocalInput(e.target.value, tz);
                                    if (iso) patch({ reminderAt: iso });
                                }}
                                onBlur={() => setPickReminder(false)}
                            />
                        ) : null}
                    </div>

                    {/* Due */}
                    <div className="px-3 py-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    type="button"
                                    className={cn(
                                        "flex w-full items-center gap-3 text-left text-sm",
                                        task.dueAt && (isOverdue(task, tz, today) ? "text-destructive" : "text-primary")
                                    )}
                                >
                                    <CalendarDays className="h-4 w-4" />
                                    <span className="flex-1">{task.dueAt ? `Due ${formatDay(task.dueAt, tz, today)}` : "Add due date"}</span>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-56">
                                <DropdownMenuItem onSelect={() => setDueKey(today)}>Today</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setDueKey(addDaysKey(today, 1))}>Tomorrow</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setDueKey(nextMonday)}>Next week</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setPickDue(true)}>Pick a date…</DropdownMenuItem>
                                {task.dueAt ? (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem className="text-destructive" onSelect={() => patch({ dueAt: null })}>
                                            Remove due date
                                        </DropdownMenuItem>
                                    </>
                                ) : null}
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {pickDue ? (
                            <Input
                                type="date"
                                autoFocus
                                defaultValue={task.dueAt ? dayKey(new Date(task.dueAt), tz) : today}
                                aria-label="Due date"
                                className="mt-2 h-8"
                                onChange={(e) => { if (/^\d{4}-\d{2}-\d{2}$/.test(e.target.value)) setDueKey(e.target.value); }}
                                onBlur={() => setPickDue(false)}
                            />
                        ) : null}
                    </div>

                    {/* Repeat */}
                    <div className="px-3 py-2">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button type="button" className={cn("flex w-full items-center gap-3 text-left text-sm", task.recurrence && "text-primary")}>
                                    <Repeat className="h-4 w-4" />
                                    <span className="flex-1">{task.recurrence ? describeRule(task.recurrence) : "Repeat"}</span>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-48">
                                <DropdownMenuItem onSelect={() => setRepeat("FREQ=DAILY")}>Daily</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setRepeat(WEEKDAYS_RULE)}>Weekdays</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setRepeat("FREQ=WEEKLY")}>Weekly</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setRepeat("FREQ=MONTHLY")}>Monthly</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setRepeat("FREQ=YEARLY")}>Yearly</DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => setCustomRepeat(true)}>Custom…</DropdownMenuItem>
                                {task.recurrence ? (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem className="text-destructive" onSelect={() => setRepeat(null)}>Never repeat</DropdownMenuItem>
                                    </>
                                ) : null}
                            </DropdownMenuContent>
                        </DropdownMenu>
                        {customRepeat ? (
                            <CustomRepeat
                                initial={parseRule(task.recurrence) ?? {
                                    freq: "WEEKLY", interval: 1,
                                    byDay: [DAY_CODES[weekdayOfKey(today) - 1] ?? "MO"], byMonthDay: null
                                }}
                                onCancel={() => setCustomRepeat(false)}
                                onSave={(rule) => { setRepeat(rule); setCustomRepeat(false); }}
                            />
                        ) : null}
                    </div>

                    {/* List */}
                    <label className="flex items-center gap-3 px-3 py-2 text-sm">
                        <ListIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="sr-only">List</span>
                        <select
                            value={task.listId ?? ""}
                            onChange={(e) => patch({ listId: e.target.value || null })}
                            className="h-8 min-w-0 flex-1 rounded-md border bg-background px-2 text-sm"
                        >
                            <option value="">Tasks</option>
                            {lists.map((l) => <option key={l.id} value={l.id}>{l.emoji ? `${l.emoji} ` : ""}{l.name}</option>)}
                        </select>
                    </label>
                </div>

                {/* Linked email */}
                {task.relatedMessageId ? (
                    <button
                        type="button"
                        onClick={() => navigate(`/mail/message/${encodeURIComponent(task.relatedMessageId ?? "")}`)}
                        className="flex w-full items-center gap-3 rounded-md border bg-background px-3 py-2.5 text-left text-sm hover:bg-accent"
                    >
                        {task.source === "flagged" ? <Flag className="h-4 w-4 text-red-500" /> : <Mail className="h-4 w-4 text-primary" />}
                        <span className="flex-1">
                            Open linked email
                            {task.source === "flagged" ? (
                                <span className="block text-xs text-muted-foreground">Completing this task unflags the message.</span>
                            ) : null}
                        </span>
                    </button>
                ) : null}

                {/* Notes */}
                <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={() => { if ((task.notes ?? "") !== notes) patch({ notes: notes.trim() ? notes.slice(0, 8000) : null }); }}
                    placeholder="Add note"
                    aria-label="Notes"
                    rows={5}
                    className="w-full resize-y rounded-md border bg-background p-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
            </div>
            <footer className="flex items-center gap-2 border-t px-3 py-2 text-xs text-muted-foreground">
                <span className="flex-1">
                    {task.isCompleted && task.completedAt
                        ? `Completed ${formatDay(task.completedAt, tz, today)}`
                        : `Created ${formatDay(task.createdAt, tz, today)}`}
                </span>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 hover:text-destructive"
                    onClick={() => onDelete(task)}
                    aria-label="Delete task"
                >
                    <Trash2 className="h-4 w-4" />
                </Button>
            </footer>
        </aside>
    );
}

function StepRow({ step, onChange, onDelete }: { step: TaskStep; onChange: (s: TaskStep) => void; onDelete: () => void }) {
    const [draft, setDraft] = useState(step.title);
    useEffect(() => setDraft(step.title), [step.title]);
    return (
        <li className="group flex items-center gap-2">
            <button
                type="button"
                onClick={() => onChange({ ...step, isCompleted: !step.isCompleted })}
                aria-label={step.isCompleted ? "Mark step incomplete" : "Mark step complete"}
                className="shrink-0 text-muted-foreground hover:text-primary"
            >
                {step.isCompleted ? <CheckCircle2 className="h-4 w-4 text-primary" /> : <Circle className="h-4 w-4" />}
            </button>
            <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => {
                    const t = draft.trim();
                    if (!t) onDelete();
                    else if (t !== step.title) onChange({ ...step, title: t.slice(0, 512) });
                }}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                aria-label="Step"
                className={cn("h-7 min-w-0 flex-1 bg-transparent text-sm outline-none", step.isCompleted && "text-muted-foreground line-through")}
            />
            <button
                type="button"
                onClick={onDelete}
                aria-label="Delete step"
                className="shrink-0 rounded p-0.5 text-muted-foreground opacity-0 hover:text-destructive focus:opacity-100 group-hover:opacity-100"
            >
                <X className="h-3.5 w-3.5" />
            </button>
        </li>
    );
}

function CustomRepeat({ initial, onSave, onCancel }: { initial: RepeatRule; onSave: (rule: string) => void; onCancel: () => void }) {
    const [rule, setRule] = useState<RepeatRule>(initial);
    return (
        <div className="mt-2 space-y-2 rounded-md border p-2 text-sm">
            <div className="flex items-center gap-2">
                <span>Every</span>
                <Input
                    type="number"
                    min={1}
                    max={999}
                    value={rule.interval}
                    onChange={(e) => setRule({ ...rule, interval: Math.min(999, Math.max(1, Number(e.target.value) || 1)) })}
                    className="h-8 w-16"
                    aria-label="Interval"
                />
                <select
                    value={rule.freq}
                    onChange={(e) => setRule({ ...rule, freq: e.target.value as RepeatFreq, byDay: [], byMonthDay: null })}
                    className="h-8 rounded-md border bg-background px-2"
                    aria-label="Unit"
                >
                    <option value="DAILY">day(s)</option>
                    <option value="WEEKLY">week(s)</option>
                    <option value="MONTHLY">month(s)</option>
                    <option value="YEARLY">year(s)</option>
                </select>
            </div>
            {rule.freq === "WEEKLY" ? (
                <div className="flex flex-wrap gap-1" role="group" aria-label="Days of the week">
                    {DAY_CODES.map((code, i) => {
                        const on = rule.byDay.includes(code);
                        return (
                            <button
                                key={code}
                                type="button"
                                aria-pressed={on}
                                onClick={() => setRule({ ...rule, byDay: on ? rule.byDay.filter((d) => d !== code) : [...rule.byDay, code] })}
                                className={cn("h-7 rounded-full border px-2 text-xs", on ? "border-primary bg-primary text-primary-foreground" : "hover:bg-accent")}
                            >
                                {DAY_SHORT[i]}
                            </button>
                        );
                    })}
                </div>
            ) : null}
            {rule.freq === "MONTHLY" ? (
                <label className="flex items-center gap-2">
                    <span>On day</span>
                    <Input
                        type="number"
                        min={1}
                        max={31}
                        value={rule.byMonthDay ?? ""}
                        placeholder="due day"
                        onChange={(e) => {
                            const d = Number(e.target.value);
                            setRule({ ...rule, byMonthDay: d >= 1 && d <= 31 ? d : null });
                        }}
                        className="h-8 w-20"
                    />
                </label>
            ) : null}
            <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={onCancel}>Cancel</Button>
                <Button size="sm" onClick={() => onSave(buildRule(rule))}>Save</Button>
            </div>
        </div>
    );
}
